import React, { useState, useCallback, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import useFeaturedCourses from "../../../hooks/useFeaturedCourses";
import { GuidedEnrollmentInlineTrigger } from "../../../components/GuidedEnrollmentDrawer";
import GuidedEnrollmentDrawer from "../../../components/GuidedEnrollmentDrawer";

// ─── Brand constants ──────────────────────────────────────────────────────────

const BRAND = {
  primary:   "#00496D",
  blue50:    "#0083D3",
  secondary: "#3A4252",
  bg:        "#FFFEF8",
  orange:    "#FE7A36",
  dark:      "#0F172A",
};

// ─── Per-card configuration ───────────────────────────────────────────────────

const CARD_CONFIG = [
  {
    dark:       false,
    accent:     BRAND.primary,
    iconBg:     "rgba(0,73,109,0.08)",
    iconColor:  BRAND.primary,
    icon:       "gavel",
    tagLabel:   "Most Popular",
    tagColor:   "#b45309",
    tagBg:      "#fef3c7",
    tagEmoji:   "🔥",
    btnBg:      BRAND.dark,
    btnColor:   "#fff",
    statBg:     "rgba(0,73,109,0.06)",
    statColor:  BRAND.primary,
    divider:    "rgba(0,73,109,0.10)",
  },
  {
    dark:       true,
    accent:     BRAND.blue50,
    iconBg:     "rgba(255,255,255,0.10)",
    iconColor:  "#fff",
    icon:       "monitor_heart",
    tagLabel:   "Highly Rated",
    tagColor:   "#059669",
    tagBg:      "rgba(16,185,129,0.15)",
    tagEmoji:   "⭐",
    btnBg:      BRAND.blue50,
    btnColor:   "#fff",
    statBg:     "rgba(255,255,255,0.08)",
    statColor:  "rgba(255,255,255,0.75)",
    divider:    "rgba(255,255,255,0.08)",
  },
];

const ROTATING_PHRASES = [
  "Communication Health Professionals",
  "Speech-Language Pathologists",
  "Audiologists",
  "Clinical Administration",
];

const TRUST_BADGES = [
  { icon: "gavel",             label: "PIPEDA & PHIPA" },
  { icon: "workspace_premium", label: "CASLPO Standards" },
  { icon: "verified_user",     label: "IPC Ontario Guidance" },
];

const TRUST_ITEMS = [
  { icon: "lock",            label: "PIPEDA & PHIPA",       sub: "Federal & Ontario health information privacy laws" },
  { icon: "verified",        label: "CASLPO Standards",     sub: "College professional practice guidelines" },
  { icon: "account_balance", label: "IPC Ontario Guidance", sub: "Information & Privacy Commissioner" },
];

// ─── CourseCard ───────────────────────────────────────────────────────────────

function CourseCard({ course, cfg, onClick }) {
  const [hovered, setHovered] = useState(false);

  const cardBg    = cfg.dark ? BRAND.dark : "#ffffff";
  const titleCol  = cfg.dark ? "#ffffff"  : BRAND.dark;
  const descCol   = cfg.dark ? "rgba(255,255,255,0.55)" : "#6b7280";
  const numCol    = cfg.dark ? "rgba(255,255,255,0.30)" : "rgba(0,73,109,0.20)";
  const starCol   = cfg.dark ? "rgba(255,255,255,0.25)" : "rgba(0,73,109,0.12)";

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`View course: ${course.course1_title.replace(/\n/g, " ")}`}
      onClick={onClick}
      onKeyDown={(e) => e.key === "Enter" && onClick()}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background:    cardBg,
        borderRadius:  24,
        border:        cfg.dark
          ? "1px solid rgba(255,255,255,0.07)"
          : "1px solid rgba(0,73,109,0.09)",
        boxShadow:     hovered
          ? cfg.dark
            ? "0 24px 60px rgba(0,0,0,0.55), 0 4px 16px rgba(0,0,0,0.30)"
            : "0 20px 56px rgba(0,73,109,0.18), 0 4px 12px rgba(0,0,0,0.06)"
          : cfg.dark
            ? "0 12px 40px rgba(0,0,0,0.40), 0 2px 8px rgba(0,0,0,0.20)"
            : "0 8px 32px rgba(0,73,109,0.10), 0 2px 8px rgba(0,0,0,0.04)",
        cursor:        "pointer",
        display:       "flex",
        flexDirection: "column",
        transition:    "transform 0.22s ease, box-shadow 0.22s ease",
        transform:     hovered ? "translateY(-5px)" : "translateY(0)",
        willChange:    "transform",
        overflow:      "hidden",
      }}
    >
      {/* ── Card header ── */}
      <div style={{ padding: "22px 22px 18px" }}>

        {/* Top row: icon + course number */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16 }}>
          <div
            aria-hidden="true"
            style={{
              width: 48,
              height: 48,
              borderRadius: 14,
              background: cfg.iconBg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i className="material-symbols-outlined" style={{ fontSize: 24, color: cfg.iconColor }}>
              {cfg.icon}
            </i>
          </div>
          <span
            aria-hidden="true"
            style={{
              fontSize: 11,
              fontWeight: 800,
              color: numCol,
              letterSpacing: "0.04em",
            }}
          >
            {String(course.id).padStart(2, "0")}
          </span>
        </div>

        {/* Course title */}
        <h3
          style={{
            fontSize: 15,
            fontWeight: 800,
            color: titleCol,
            lineHeight: 1.4,
            margin: "0 0 14px",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {course.course1_title.replace(/\n/g, " ")}
        </h3>

        {/* Badge row */}
        <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              padding: "3px 10px",
              borderRadius: 100,
              background: cfg.tagBg,
              color: cfg.tagColor,
            }}
          >
            {cfg.tagEmoji} {cfg.tagLabel}
          </span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              padding: "3px 10px",
              borderRadius: 100,
              background: cfg.dark ? "rgba(34,197,94,0.15)" : "rgba(34,197,94,0.10)",
              color: "#16a34a",
            }}
          >
            Enrolling Now
          </span>
        </div>
      </div>

      {/* ── Divider ── */}
      <div style={{ height: 1, background: cfg.divider, margin: "0 22px" }} aria-hidden="true" />

      {/* ── Card body ── */}
      <div style={{ padding: "16px 22px 20px", flex: 1, display: "flex", flexDirection: "column", gap: 12 }}>

        {/* Inline stats */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          {[
            { icon: "view_module", val: course.course1_modules, label: "Key Modules" },
            { icon: "menu_book",   val: course.course1_lessons,  label: "Expert Lessons" },
            { icon: "schedule",    val: "12 Weeks",              label: "Week Format" },
          ].map((m, idx) => (
            <React.Fragment key={m.label}>
              {idx > 0 && (
                <span style={{ fontSize: 9, color: cfg.dark ? "rgba(255,255,255,0.20)" : "rgba(0,73,109,0.25)" }} aria-hidden="true">•</span>
              )}
              <div
                title={m.label}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <i
                  className="material-symbols-outlined"
                  style={{ fontSize: 12, color: cfg.statColor }}
                  aria-hidden="true"
                >
                  {m.icon}
                </i>
                <span style={{ fontSize: 12, fontWeight: 600, color: cfg.statColor }}>{m.val}</span>
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* Description snippet */}
        <p
          style={{
            fontSize: 12,
            color: descCol,
            lineHeight: 1.65,
            margin: 0,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {course.course1_Overview1}
        </p>

        {/* Footer: rating + CTA */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginTop: 4 }}>
          {/* Star rating */}
          <div
            style={{ display: "flex", alignItems: "center", gap: 2 }}
            aria-label="Rating: 4.7 out of 5 stars"
          >
            {[1, 2, 3, 4].map((s) => (
              <i key={s} className="ri-star-fill" style={{ fontSize: 12, color: "#FFC444" }} aria-hidden="true" />
            ))}
            <i className="ri-star-half-fill" style={{ fontSize: 12, color: "#FFC444" }} aria-hidden="true" />
            <span style={{ fontSize: 11, color: starCol, marginLeft: 3, fontWeight: 600 }}>4.7</span>
          </div>

          {/* CTA */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              background: cfg.btnBg,
              color: cfg.btnColor,
              fontSize: 11,
              fontWeight: 700,
              padding: "7px 14px",
              borderRadius: 100,
              whiteSpace: "nowrap",
              letterSpacing: "0.01em",
            }}
          >
            View Course
            <i className="material-symbols-outlined" style={{ fontSize: 13 }} aria-hidden="true">arrow_forward</i>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Course Concierge floating bubble ────────────────────────────────────────

function ConciergeBubble() {
  const [open, setOpen]       = useState(false);
  const [visible, setVisible] = useState(false);

  // Fade in after mount so it doesn't flash immediately
  React.useEffect(() => {
    const t = setTimeout(() => setVisible(true), 800);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      <div
        style={{
          position:   "fixed",
          bottom:     88,
          right:      20,
          zIndex:     7999,
          opacity:    visible ? 1 : 0,
          transform:  visible ? "translateY(0) scale(1)" : "translateY(12px) scale(0.92)",
          transition: "opacity 0.4s ease, transform 0.4s ease",
          pointerEvents: visible ? "auto" : "none",
        }}
        aria-label="Open course concierge"
      >
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={{
            display:      "flex",
            alignItems:   "center",
            gap:          10,
            background:   "#ffffff",
            border:       "1px solid rgba(0,73,109,0.12)",
            borderRadius: 100,
            padding:      "10px 16px 10px 12px",
            boxShadow:    "0 8px 32px rgba(0,73,109,0.18), 0 2px 8px rgba(0,0,0,0.08)",
            cursor:       "pointer",
            maxWidth:     220,
          }}
        >
          {/* Animated dot + icon */}
          <div
            style={{
              width:          36,
              height:         36,
              borderRadius:   "50%",
              background:     `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.blue50})`,
              display:        "flex",
              alignItems:     "center",
              justifyContent: "center",
              flexShrink:     0,
              position:       "relative",
            }}
          >
            <i className="material-symbols-outlined" style={{ fontSize: 18, color: "#fff" }} aria-hidden="true">
              smart_toy
            </i>
            {/* Live pulse */}
            <span
              aria-hidden="true"
              style={{
                position:     "absolute",
                top:          -2,
                right:        -2,
                width:        10,
                height:       10,
                borderRadius: "50%",
                background:   "#22c55e",
                border:       "2px solid #fff",
              }}
            />
          </div>

          {/* Text */}
          <div style={{ textAlign: "left", lineHeight: 1.3 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: BRAND.dark }}>Course Concierge</div>
            <div style={{ fontSize: 11, color: "#6b7280" }}>Not sure where to start?</div>
          </div>
        </button>
      </div>

      {open && <GuidedEnrollmentDrawer onClose={() => setOpen(false)} />}
    </>
  );
}

// ─── TrustBar (below hero) ────────────────────────────────────────────────────

function TrustBar() {
  return (
    <section
      style={{
        background:   "rgba(0,73,109,0.03)",
        borderTop:    "1px solid rgba(0,73,109,0.08)",
        borderBottom: "1px solid rgba(0,73,109,0.08)",
        padding:      "40px 0 44px",
      }}
      aria-label="Regulatory trust foundations"
    >
      <div className="container cmw-1704">
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <p
            style={{
              fontSize:      11,
              fontWeight:    700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color:         BRAND.blue50,
              margin:        "0 0 10px",
            }}
          >
            Regulatory Foundations
          </p>
          <h2
            style={{
              fontSize:   22,
              fontWeight: 800,
              color:      BRAND.dark,
              margin:     "0 0 10px",
              lineHeight: 1.3,
            }}
          >
            Built on Trusted Regulatory Foundations
          </h2>
          <p
            style={{
              fontSize:   15,
              color:      "#6b7280",
              maxWidth:   560,
              margin:     "0 auto",
              lineHeight: 1.65,
            }}
          >
            Aligned with PIPEDA, PHIPA, IPC Ontario guidance, and CASLPO professional standards
            to support compliant clinical practice.
          </p>
        </div>

        <div
          style={{
            display:        "flex",
            justifyContent: "center",
            gap:            20,
            flexWrap:       "wrap",
          }}
        >
          {TRUST_ITEMS.map((item) => (
            <div
              key={item.label}
              style={{
                display:    "flex",
                alignItems: "flex-start",
                gap:        14,
                background: "#fff",
                border:     "1px solid rgba(0,73,109,0.08)",
                borderRadius: 16,
                padding:    "20px 24px",
                boxShadow:  "0 2px 12px rgba(0,73,109,0.06)",
                minWidth:   200,
                flex:       "1 1 200px",
                maxWidth:   320,
              }}
            >
              <div
                style={{
                  width:          44,
                  height:         44,
                  borderRadius:   12,
                  background:     "rgba(0,73,109,0.07)",
                  display:        "flex",
                  alignItems:     "center",
                  justifyContent: "center",
                  flexShrink:     0,
                }}
                aria-hidden="true"
              >
                <i className="material-symbols-outlined" style={{ fontSize: 22, color: BRAND.primary }}>
                  {item.icon}
                </i>
              </div>
              <div>
                <div
                  style={{
                    fontSize:     14,
                    fontWeight:   700,
                    color:        BRAND.primary,
                    marginBottom: 4,
                    lineHeight:   1.3,
                  }}
                >
                  {item.label}
                </div>
                <div style={{ fontSize: 12, color: "#9ca3af", lineHeight: 1.5 }}>{item.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── HeroBanner (main export) ─────────────────────────────────────────────────

export default function HeroBanner() {
  const { featured, trackClick } = useFeaturedCourses();
  const navigate = useNavigate();

  const [phraseIdx, setPhraseIdx] = useState(0);
  const [visible,   setVisible]   = useState(true);

  useEffect(() => {
    const id = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setPhraseIdx(i => (i + 1) % ROTATING_PHRASES.length);
        setVisible(true);
      }, 320);
    }, 5000);
    return () => clearInterval(id);
  }, []);

  const handleCardClick = useCallback(
    (course) => {
      trackClick(course.id);
      navigate(`/course/${course.id}`);
    },
    [trackClick, navigate]
  );

  return (
    <>
      {/* ── Hero section ── */}
      <section
        className="bg-fffef8 position-relative z-1"
        style={{ paddingTop: 56, paddingBottom: 72, overflow: "hidden" }}
        aria-label="Hero banner"
      >
        {/* Ambient glow */}
        <div
          aria-hidden="true"
          style={{
            position:     "absolute",
            top:          "-10%",
            right:        "-12%",
            width:        700,
            height:       700,
            borderRadius: "50%",
            background:   "radial-gradient(circle, rgba(0,131,211,0.07) 0%, rgba(0,73,109,0.03) 50%, transparent 70%)",
            pointerEvents: "none",
            zIndex:       0,
          }}
        />
        <div
          aria-hidden="true"
          style={{
            position:     "absolute",
            bottom:       "-5%",
            left:         "-5%",
            width:        400,
            height:       400,
            borderRadius: "50%",
            background:   "radial-gradient(circle, rgba(254,122,54,0.04) 0%, transparent 70%)",
            pointerEvents: "none",
            zIndex:       0,
          }}
        />

        <div className="container cmw-1308" style={{ position: "relative", zIndex: 1 }}>
          <div className="row g-4 align-items-center">

            {/* ── Left column: copy ── */}
            <div className="col-12 col-lg-6">
              <div style={{ paddingRight: "clamp(0px, 2vw, 24px)", paddingTop: 8 }}>

                {/* Pill label */}
                <div style={{ marginBottom: 28 }}>
                  <span
                    style={{
                      display:     "inline-flex",
                      alignItems:  "center",
                      gap:         8,
                      background:  "#ffef99",
                      padding:     "6px 18px",
                      borderRadius: 50,
                      fontSize:    13,
                      fontWeight:  600,
                      color:       BRAND.secondary,
                    }}
                  >
                    <i
                      className="material-symbols-outlined"
                      style={{ fontSize: 16, color: "#ff4023" }}
                      aria-hidden="true"
                    >
                      menu_book
                    </i>
                    Learn anytime, Anywhere
                  </span>
                </div>

                {/* H1 */}
                <h1
                  style={{
                    fontSize:      "clamp(28px, 4.2vw, 50px)",
                    fontWeight:    800,
                    lineHeight:    1.18,
                    letterSpacing: "-0.03em",
                    color:         BRAND.dark,
                    margin:        "0 0 22px",
                  }}
                >
                  <span style={{ color: BRAND.blue50 }}>
                    Healthcare Privacy &amp; AI Compliance
                  </span>{" "}
                  Training for{" "}
                  <span
                    style={{
                      display:       "inline-block",
                      position:      "relative",
                      verticalAlign: "top",
                      color:         BRAND.primary,
                    }}
                  >
                    {/* Ghost — longest phrase reserves constant height so layout never shifts */}
                    <span aria-hidden="true" style={{ visibility: "hidden", display: "block" }}>
                      Communication Health Professionals
                    </span>
                    {/* Animated text overlaid on the ghost */}
                    <span
                      aria-live="polite"
                      aria-atomic="true"
                      style={{
                        position:   "absolute",
                        top:        0,
                        left:       0,
                        width:      "100%",
                        opacity:    visible ? 1 : 0,
                        transform:  visible ? "translateY(0px)" : "translateY(10px)",
                        transition: visible
                          ? "opacity 0.72s ease, transform 0.72s cubic-bezier(0.22, 1, 0.36, 1)"
                          : "opacity 0.28s ease, transform 0.28s ease",
                      }}
                    >
                      {ROTATING_PHRASES[phraseIdx]}
                    </span>
                  </span>
                </h1>

                {/* Subtext */}
                <p
                  style={{
                    fontSize:   "clamp(15px, 1.7vw, 17px)",
                    color:      "#4b5563",
                    lineHeight: 1.7,
                    maxWidth:   580,
                    margin:     "0 0 36px",
                  }}
                >
                  Specialized certification programs designed for Speech-Language Pathologists,
                  Audiologists, and clinical teams in Canada, aligned with PIPEDA, PHIPA, IPC guidance,
                  and CASLPO standards.
                </p>

                {/* CTA buttons */}
                <div
                  style={{
                    display:     "flex",
                    flexWrap:    "wrap",
                    alignItems:  "center",
                    gap:         20,
                    marginBottom: 8,
                  }}
                >
                  <Link
                    to="/courses"
                    className="btn landing-page-btn btn-primary-50 fw-medium rounded-2 d-inline-flex align-items-center"
                    style={{ gap: 9, fontSize: 15, padding: "11px 24px" }}
                  >
                    <i className="material-symbols-outlined" style={{ fontSize: 20 }} aria-hidden="true">
                      school
                    </i>
                    Explore Courses
                  </Link>

                </div>

                {/* Guided enrollment inline trigger */}
                <GuidedEnrollmentInlineTrigger />

                {/* Trust badges row */}
                <div
                  className="hero-trust-row"
                  style={{
                    display:     "flex",
                    flexWrap:    "wrap",
                    alignItems:  "center",
                    gap:         20,
                    marginTop:   32,
                    paddingTop:  24,
                    borderTop:   "1px solid rgba(0,73,109,0.08)",
                  }}
                  aria-label="Compliance certifications"
                >
                  {TRUST_BADGES.map((badge) => (
                    <div key={badge.label} style={{ display: "flex", alignItems: "center", gap: 7 }}>
                      <div
                        style={{
                          width:          30,
                          height:         30,
                          borderRadius:   8,
                          background:     "rgba(0,73,109,0.07)",
                          display:        "flex",
                          alignItems:     "center",
                          justifyContent: "center",
                          flexShrink:     0,
                        }}
                        aria-hidden="true"
                      >
                        <i className="material-symbols-outlined" style={{ fontSize: 15, color: BRAND.primary }}>
                          {badge.icon}
                        </i>
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 600, color: BRAND.secondary, whiteSpace: "nowrap" }}>
                        {badge.label}
                      </span>
                    </div>
                  ))}
                </div>

              </div>
            </div>

            {/* ── Right column: featured course cards ── */}
            <div className="col-12 col-lg-6">
              <div style={{ padding: "8px 0 8px 8px" }}>

                {/* Section header */}
                <div
                  style={{
                    display:        "flex",
                    alignItems:     "center",
                    justifyContent: "space-between",
                    marginBottom:   24,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span
                      aria-hidden="true"
                      style={{
                        width:        8,
                        height:       8,
                        borderRadius: "50%",
                        background:   "#22c55e",
                        display:      "inline-block",
                        boxShadow:    "0 0 0 3px rgba(34,197,94,0.25)",
                      }}
                    />
                    <span
                      style={{
                        fontSize:      11,
                        fontWeight:    700,
                        color:         "#6b7280",
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                      }}
                    >
                      Featured Courses
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate("/courses")}
                    style={{
                      background:         "none",
                      border:             "none",
                      fontSize:           12,
                      fontWeight:         600,
                      color:              BRAND.blue50,
                      cursor:             "pointer",
                      padding:            0,
                      textDecoration:     "underline",
                      textUnderlineOffset: 3,
                    }}
                  >
                    View all courses →
                  </button>
                </div>

                {/* Cards — stacked vertically, Card 2 offset right */}
                <div
                  className="hero-cards-stack"
                  style={{ display: "flex", flexDirection: "column", gap: 16 }}
                  aria-label="Featured course cards"
                >
                  {featured.map((course, i) => (
                    <div
                      key={course.id}
                      className={i === 1 ? "hero-card-offset" : ""}
                      style={{ marginLeft: i === 1 ? "clamp(20px, 6%, 48px)" : 0 }}
                    >
                      <CourseCard
                        course={course}
                        cfg={CARD_CONFIG[i]}
                        onClick={() => handleCardClick(course)}
                      />
                    </div>
                  ))}
                </div>


              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── Trust bar ── */}
      <TrustBar />

      {/* ── Course Concierge floating bubble ── */}
      <ConciergeBubble />

      {/* ── Responsive overrides ── */}
      <style>{`
        @media (max-width: 991px) {
          .hero-card-offset {
            margin-left: 0 !important;
          }
        }
        @media (max-width: 767px) {
          .hero-trust-row {
            flex-direction: column !important;
            align-items: flex-start !important;
          }
        }
      `}</style>
    </>
  );
}
